// DemoTreeCommunity.tsx
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridApi, ICellRendererParams, RowSelectedEvent, SelectionChangedEvent } from 'ag-grid-community';
// Nếu bạn dùng Ant Design icon:
import { EyeOutlined, EditOutlined, DeleteOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { IGroupTypeList } from '../../../../api/groupCustomerV2/groupCustomerV2Api';
import { formatPrice, getTitleSelectOption } from '../../../../until';
import { dataTypeGroup } from '../../../../services/data';
import { NodeEvent } from './SideBar';
import { NodataTable } from '../../../../components/NoDataTable';
import { PERMISSION } from './AddGroup/Component/AG_Decentralization';
import { useSelector } from 'react-redux';
import { selectorInfoUser } from '../../../../store/slices/infoUserSlice';

// ===== Helpers =====
const INDENT_STEP = 16;
const indentPx = (level?: number) => Math.max(0, level ?? 0) * INDENT_STEP;

function hasChildren(n?: IGroupTypeList) {
    return !!(n && n.children && n.children.length > 0);
}

/** Flatten theo trạng thái expanded; giữ nguyên thứ tự phần tử như trong mảng gốc */
function flattenTree(tree: IGroupTypeList[], expanded: Set<number>): IGroupTypeList[] {
    const out: IGroupTypeList[] = [];
    const dfs = (nodes: IGroupTypeList[]) => {
        for (const n of nodes) {
            out.push(n);
            if (hasChildren(n) && expanded.has(n.id)) {
                dfs(n.children);
            }
        }
    };
    dfs(tree);
    return out;
}

// ===== Cell Renderers =====
const ToggleCell: React.FC<
    ICellRendererParams & {
        onToggle: (id: number) => void;
        expanded: boolean;
        hasChildren: boolean;
    }
> = (p) => {
    const { data } = p as any;
    const level = (data?.level ?? 0);
    const { expanded, hasChildren, onToggle } = p as any;

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) onToggle(data.id);
    };



    return (
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {/* indent */}
            <span style={{ width: indentPx(level), display: 'flex', flexShrink: 0 }} />
            {/* caret */}
            {hasChildren ? (
                <button
                    onClick={handleToggle}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        width: 22,
                        textAlign: 'center',
                        padding: 0,
                        lineHeight: 1,
                        display: 'flex', flexShrink: 0
                    }}
                    aria-label={expanded ? 'Thu gọn' : 'Mở rộng'}
                    title={expanded ? 'Thu gọn' : 'Mở rộng'}
                >
                    {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
            ) : (
                <span style={{ width: 22, display: 'flex', flexShrink: 0 }} />
            )}
            {/* title */}
            <span ><span style={{ width: 6, display: 'flex', flexShrink: 0 }}></span> {data?.title}</span>
        </div>
    );
};

// ===== Main Grid =====
export default function GroupCustomerTypeTree({
    add,
    edit,
    isDelete,
    selectedIdGroup,
    dataTable,
    defaultExpandLevel = 1,
    onAction,
    setSelectedIdGroup
}: {
    add: boolean,
    edit: boolean,
    isDelete: boolean,
    selectedIdGroup: Set<Number>;
    dataTable: IGroupTypeList[];
    defaultExpandLevel?: number;

    onAction?: (row: NodeEvent) => void;
    setSelectedIdGroup: (ids: Set<Number>) => void;
}) {

  const { id:  idLogin } = useSelector(selectorInfoUser)

    //#region Phần chọn nhóm
    const isRestoringSelection = useRef(false);
    const gridApiRef = useRef<GridApi | null>(null);

    // --- thêm dưới phần helpers/ useMemo ---
    const idToDescendants = useMemo(() => {
        const map = new Map<number, number[]>();

        const collectDesc = (n: IGroupTypeList): number[] => {
            if (!hasChildren(n)) {
                map.set(n.id, []);
                return [];
            }
            const acc: number[] = [];
            for (const c of n.children) {
                acc.push(c.id);
                const sub = collectDesc(c);
                if (sub.length) acc.push(...sub);
            }
            map.set(n.id, acc);
            return acc;
        };

        const walk = (nodes: IGroupTypeList[]) => {
            for (const n of nodes) {
                collectDesc(n);
                if (hasChildren(n)) walk(n.children);
            }
        };
        walk(dataTable);
        return map;
    }, [dataTable]);

    // --- thêm trong component ---
    const onRowSelected = useCallback((event: RowSelectedEvent) => {
        const api = event.api;
        const node = event.node;
        if (!node || !node.data || isRestoringSelection.current) return;

        const id = Number(node.data.id);
        const descendants = idToDescendants.get(id) ?? [];
        if (descendants.length === 0) return; // không có con => thôi

        // Tránh vòng lặp sự kiện
        isRestoringSelection.current = true;

        // Set selected cho toàn bộ hậu duệ theo trạng thái của parent
        const shouldSelect = node.isSelected();
        const descendantSet = new Set(descendants);

        api.forEachNode((n: any) => {
            if (!n.data) return;
            const nid = Number(n.data.id);
            if (descendantSet.has(nid)) {
                n.setSelected(shouldSelect, false, true);
            }
        });

        // Cập nhật state selectedIdGroup
        const next = new Set(selectedIdGroup);
        if (shouldSelect) {
            next.add(id);
            descendants.forEach(d => next.add(d));
        } else {
            next.delete(id);
            descendants.forEach(d => next.delete(d));
        }
        setSelectedIdGroup(next);

        // Nhả cờ
        // dùng setTimeout 0 để đợi ag-grid bắn xong các event nội bộ
        setTimeout(() => { isRestoringSelection.current = false; }, 0);
    }, [idToDescendants, selectedIdGroup, setSelectedIdGroup]);



    // Khởi tạo expandedIds: mở sẵn các node có level < defaultExpandLevel và có children
    const [expandedIds, setExpandedIds] = useState<Set<number>>(() => {
        const s = new Set<number>();
        const scan = (nodes: IGroupTypeList[]) => {
            for (const n of nodes) {
                if (hasChildren(n) && (n.level ?? 0) < defaultExpandLevel) s.add(n.id);
                if (hasChildren(n)) scan(n.children);
            }
        };
        scan(dataTable);
        return s;
    });

    // rowData hiển thị
    const rowData = useMemo(() => flattenTree(dataTable, expandedIds), [dataTable, expandedIds]);

    //------------------------------------ Start SELECT
    // Định danh duy nhất cho mỗi row
    const getRowId = useCallback((p: { data: any }) => String(p.data.id), []);

    // --- Grid ready ---
    const onGridReady = useCallback((params: { api: GridApi }) => {
        gridApiRef.current = params.api;
    }, []);

    // --- Selection change ---
    const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
        // if (isRestoringSelection.current) return;

        // const api = event.api;
        // const selectedNodes = api.getSelectedNodes().filter(n => n.data);
        // const selectedIdsOnPage = selectedNodes.map(n => Number(n.data.id));

        // const currentPageIds = (rowData ?? []).map((row: any) => Number(row.id));

        // // --- Tạo bản sao của selectedIds hiện tại ---
        // const nextSet = new Set(selectedIdGroup);

        // // --- Xác định phần tử được thêm và bị xóa trên trang HIỆN TẠI ---
        // const added: number[] = [];
        // const removed: number[] = [];

        // // Kiểm tra từng ID trên trang hiện tại
        // for (const id of currentPageIds) {
        //     const isCurrentlySelected = selectedIdsOnPage.includes(id);
        //     const wasPreviouslySelected = selectedIdGroup.has(id);

        //     if (isCurrentlySelected && !wasPreviouslySelected) {
        //         // ID mới được chọn
        //         added.push(id);
        //         nextSet.add(id);
        //     } else if (!isCurrentlySelected && wasPreviouslySelected) {
        //         // ID bị bỏ chọn
        //         removed.push(id);
        //         nextSet.delete(id);
        //     }
        // }

        // setSelectedIdGroup(nextSet);



        if (isRestoringSelection.current) return;

        const api = event.api;
        const selectedNodes = api.getSelectedNodes().filter(n => n.data);
        const selectedIdsOnPage = selectedNodes.map(n => Number(n.data.id));

        const currentPageIds = (rowData ?? []).map((row: any) => Number(row.id));

        const nextSet = new Set(selectedIdGroup);

        for (const id of currentPageIds) {
            const selectedNow = selectedIdsOnPage.includes(id);
            const was = selectedIdGroup.has(id);
            if (selectedNow && !was) nextSet.add(id);
            else if (!selectedNow && was) nextSet.delete(id);
        }

        setSelectedIdGroup(nextSet);

        // 👉 Cập nhật danh sách selected account
    }, [selectedIdGroup, rowData]);



    // --- Khôi phục selection khi data mới load ---
    useEffect(() => {
        if (!gridApiRef.current || !rowData?.length) return;

        isRestoringSelection.current = true;

        // Xây 1 set bao gồm cả các hậu duệ của những id đang được chọn
        const expandedSelection = selectedIdGroup;
        for (const id of selectedIdGroup) {
            const desc = idToDescendants.get(Number(id)) ?? [];
            desc.forEach(d => expandedSelection.add(d));
        }

        gridApiRef.current.forEachNode((node: any) => {
            if (!node.data) return;
            const id = Number(node.data.id);
            node.setSelected(expandedSelection.has(id), false, true);
        });

        setTimeout(() => { isRestoringSelection.current = false; }, 0);
    }, [rowData, selectedIdGroup, idToDescendants]);

    //------------------------------------ End SELECT

    const onToggle = useCallback((id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    // rebind khi rowData đổi

    //#region Cấu hình cột nhóm
    const columnDefs = useMemo<ColDef[]>(() => [
        {
            headerName: 'Hành động',
            headerComponentParams: {
                template: '<span class="w-full flex items-center justify-center">Hành động</span>'
            },
            field: 'action',
            width: 160,
            suppressMenu: true,
            sortable: false,
            resizable: false,
            cellRenderer: (p: any) => {
                const data: IGroupTypeList = p.data;
                const { id, level, parentId, title, permissionType } = data;

                // helper tạo event object
                const makeEvent = (action: "add" | "update" | "view" | "delete" | "combine"| "clone") => ({
                    action,
                    item: {
                        key: id.toString(),
                        level,
                        title,
                        parentId,
                    },
                });

                const emit = (action: NodeEvent['action']) => {
                    const event = makeEvent(action);
                    onAction?.(event)
                };

                return (
                    <div className="w-full h-full flex items-center gap-4 justify-center">
                        <EyeOutlined
                            style={{ cursor: 'pointer' }}
                            onClick={() => emit('view')}
                        />
                        <EditOutlined
                            className={
                                idLogin !== 1 && (!edit || permissionType === PERMISSION.VIEW)
                                    ? 'disabled'
                                    : ''
                            }
                            style={{ cursor: 'pointer' }}
                            onClick={() => emit('update')}
                        />
                        <DeleteOutlined
                            className={
                                idLogin !== 1 && (!isDelete || permissionType !== PERMISSION.DELETE)
                                    ? 'disabled'
                                    : ''
                            }
                            // disabled={!isDelete}
                            style={{ cursor: 'pointer', color: '#F5222D' }}
                            onClick={() => emit('delete')}
                        />
                    </div>
                );
            }
        },
        {
            headerName: 'Tên nhóm',
            field: 'title',
            flex: 1,
            minWidth: 280,
            // cellRenderer: "agGroupCellRenderer",
            sortable: false, // để giữ thứ tự cây; muốn sort sibling thì viết comparator tùy biến
            // cellRendererParams: {
            cellRenderer: (p: ICellRendererParams) => {
                const node = p.data as IGroupTypeList;
                const _hasChildren = hasChildren(node);
                const expanded = expandedIds.has(node.id);
                return (
                    <ToggleCell
                        {...p}
                        hasChildren={_hasChildren}
                        expanded={expanded}
                        onToggle={onToggle}
                    />
                );
            },
            // }
            // cellRenderer:
        },
        {
            headerName: 'Số lượng',
            field: 'customerCount',
            width: 120,
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (p: any) => {
                const customerCount = p.data.customerCount ?? 0
                return (
                    <span>
                        {formatPrice(customerCount)}
                    </span>
                );
            },
        },
        {
            headerName: 'Loại nhóm',
            field: 'groupTypeName',
            width: 160,
            cellRenderer: (p: any) => {
                const status = p.data.status ?? undefined
                return (
                    <span>
                        {getTitleSelectOption(dataTypeGroup, status)}
                    </span>
                );
            },
        },
        {
            headerName: 'Mô tả',
            field: 'description',
            flex: 1,
            minWidth: 220,
            tooltipField: 'description',
            cellClass: 'ag-left-aligned-cell',
        },
        {
            headerName: 'Ngày tạo',
            field: 'timestamp',
            width: 130,
        },
        {
            headerName: 'Người tạo',
            field: 'adminName',
            width: 180,
        },
    ], [dataTable, expandedIds, onToggle, onAction]);

    //#endregion

    useEffect(() => {
        gridApiRef.current?.setGridOption('rowData', rowData);
    }, [rowData]);
    //#endregion

    return (
        <>

            <div className={`order-history-card__body `}>
                <div className="ag-theme-alpine  custom-table-ag" style={{ width: '100%', height: 610 }}>
                    <AgGridReact
                        className="my-auto-height-grid"
                        getRowStyle={(params: any) => ({
                            backgroundColor: params?.node?.rowIndex % 2 ? '#f7f9fc' : '#ffffff',
                        })}
                        treeData={true}
                        getDataPath={(data) => data.path} // hoặc children logic của bạn
                        groupDisplayType="singleColumn"

                        rowHeight={55}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        alwaysShowHorizontalScroll
                        alwaysShowVerticalScroll
                        rowSelection={{ mode: 'multiRow' }} selectionColumnDef={{ pinned: 'left', lockPosition: 'left', suppressMovable: true }}
                        overlayNoRowsTemplate={NodataTable}
                        getRowId={getRowId}
                        onGridReady={onGridReady}
                        onSelectionChanged={onSelectionChanged}
                        onRowSelected={onRowSelected}
                    />
                </div>
            </div>
        </>
    );
}
